import { SxProps, useMediaQuery } from '@mui/material';
import { Photo } from '../../../interfaces/media.interface';
import { useThemeContext } from '../../../theme/ThemeContextProvider';
import BaseModal from '../../MUIComponents/BaseModal';
import { ElementIdEnum } from '../../../enums/element-id-enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { SnackbarSeverityEnum } from '../../../enums/snackbar-severity-enum';
import PhotoGrid from '../Photos/components/photos-tab/PhotoGrid';
import UploadedFileIconElement from '../../Auth/Elements/UploadedFileIconElement';
import DragAndDropBox from '../../MUIComponents/DragAndDropBox/DragAndDropBox';
import { triggerFileInput } from '../../../utils/helper/triggerInputHelper';
import {
  isSupportedIdConfirmationFileHelper,
  validateFileSize,
} from '../../../utils/helper/file-validation-helper';
import { useAddUserPhotoMutation } from '../../../store/apiSlice/profileMediaApi';
import PhotosIcon from '../../Icons/PhotosIcon';
import GridSkeleton from '../GridSkeleton';

interface AddPhotosModalProps {
  albumId?: string | null;
  onClose: () => void;
  isOpen: boolean;
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>;
  setSnackbarMessage: Dispatch<SetStateAction<string>>;
  setSnackbarSeverity: Dispatch<SetStateAction<SnackbarSeverityEnum>>;
}

const AddPhotosModal = ({
  albumId = null,
  onClose,
  isOpen = false,
  setSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
}: AddPhotosModalProps) => {
  const { theme } = useThemeContext();
  const tabletAndBigger = useMediaQuery(theme.breakpoints.up('lg'));
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [files, setFiles] = useState<File[]>([]);

  // eslint-disable-next-line
  const [dragOver, setDragOver] = useState(false);

  const [addUserPhoto, { isLoading: isUpdatingFiles }] =
    useAddUserPhotoMutation();

  const photosRef = useRef<Photo[]>([]);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(() => {
    return () => {
      photosRef.current.forEach((photo) => {
        if (photo.fileUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(photo.fileUrl);
        }
      });
    };
  }, []);

  const handleFileRemove = async (id: string) => {
    const photoToRemove = photos.find((photo) => photo.id === id);
    if (photoToRemove?.fileUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(photoToRemove.fileUrl);
    }
    setPhotos((prevPhotos) => prevPhotos.filter((photo) => photo.id !== id));
    setSnackbarMessage('Photo deleted successfully');
    setSnackbarSeverity(SnackbarSeverityEnum.SUCCESS);
    setSnackbarOpen(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const addFiles = async (files: FileList) => {
    const newFiles = Array.from(files);
    if (newFiles.length + photos.length > 5) {
      setSnackbarMessage('You can upload up to 5 files at once');
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      return;
    }

    setFiles(newFiles);

    const newPhotos: Photo[] = Array.from(files).map((file) => {
      const url = URL.createObjectURL(file);
      return {
        id: crypto.randomUUID(),
        fileUrl: url,
      };
    });

    setPhotos((prevPhotos) => [...prevPhotos, ...newPhotos]);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFiles(event.target.files);
      event.target.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    if (event.dataTransfer.files) {
      addFiles(event.dataTransfer.files);
    }
  };

  const boxSx: SxProps = {
    width: { xs: '100%', lg: '875px !important' },
    maxHeight: { xs: '80vh', lg: '90dvh' },
    p: { xs: '20px 12px 12px', lg: '36px 36px 48px' },
    position: { xs: 'fixed', lg: 'absolute' },
    top: { lg: '50%' },
    left: { lg: '50%' },
    bottom: { xs: 0, lg: 'unset' },
    borderRadius: { lg: '0 !important' },
    transform: { lg: 'translate(-50%, -50%)' },
    '.content': { m: '48px 0 36px' },
  };

  const closeModal = () => {
    photos.forEach((photo) => {
      if (photo.fileUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(photo.fileUrl);
      }
    });
    setPhotos([]);
    setFiles([]);
    onClose();
  };

  const publishChanges = async () => {
    if (!files?.length) {
      return;
    }

    // Check file size first
    for (const file of files) {
      const sizeValidation = validateFileSize(file);
      if (!sizeValidation.valid) {
        setSnackbarMessage(
          sizeValidation.error || 'File size validation failed'
        );
        setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
        setSnackbarOpen(true);
        return;
      }
    }

    const validFiles = files.filter((file) =>
      isSupportedIdConfirmationFileHelper(file)
    );
    const invalidFiles = files.filter(
      (file) => !isSupportedIdConfirmationFileHelper(file)
    );

    if (validFiles.length > 5) {
      setSnackbarMessage('You can upload up to 5 files at once');
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      return;
    }

    if (invalidFiles.length > 0) {
      setSnackbarMessage('Invalid file type. Only images (JPEG, PNG, WebP)');
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      return;
    }
    try {
      await Promise.all(
        validFiles.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);
          albumId && formData.append('photoAlbumId', albumId);
          await addUserPhoto(formData).unwrap();
        })
      );
      setSnackbarMessage('All files uploaded successfully');
      setSnackbarSeverity(SnackbarSeverityEnum.SUCCESS);
      setSnackbarOpen(true);
    } catch (error: any) {
      const errorMessage =
        error?.data?.message || error?.message || 'Failed to upload files';
      setSnackbarMessage(`Error uploading files: ${errorMessage}`);
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      console.error('Error uploading files:', error);
    }

    closeModal();
  };

  const mainContent = !photos.length ? (
    <DragAndDropBox
      handleDragOver={handleDragOver}
      handleDragLeave={handleDragLeave}
      handleDrop={handleDrop}
      handleFileChange={handleFileChange}
      triggerFileInput={() => triggerFileInput(ElementIdEnum.ADD_PHOTOS_MODAL)}
      fileInputId={ElementIdEnum.ADD_PHOTOS_MODAL}
      text={
        <>
          Drag and drop your photos here
          <br />
          You can upload up to 5 files. Only images (JPEG, PNG, JPG, WebP) are
          accepted.
        </>
      }
      icon={
        <UploadedFileIconElement>
          <PhotosIcon active />
        </UploadedFileIconElement>
      }
      isVideoInput={false}
    />
  ) : (
    <PhotoGrid files={photos} handleFileRemove={handleFileRemove} />
  );

  return (
    <BaseModal
      boxSx={{ ...boxSx }}
      disableEscapeButton
      disableContentMargin
      isOpen={isOpen}
      toggle={closeModal}
      onAccept={publishChanges}
      footer={{
        okText: 'publish',
        declineText: 'cancel',
        styles: {
          gap: 'unset',
        },
        declineButtonStyles: {
          flex: 'unset !important',
          borderColor: theme.palette.background.darker,
          minWidth: tabletAndBigger ? '220px' : 'unset',
          borderRadius: 0,
        },
        okButtonStyles: {
          flex: 'unset !important',
          minWidth: tabletAndBigger ? '220px' : 'unset',
          borderRadius: 0,
        },
        isOkButtonDisabled:
          photos?.length === 0 || isUpdatingFiles ? true : false,
      }}
      header={{ text: 'Upload Photo' }}
    >
      {isUpdatingFiles ? (
        <GridSkeleton itemCount={photos.length || 1} />
      ) : (
        mainContent
      )}
    </BaseModal>
  );
};

export default AddPhotosModal;
